import { SegmentedControls } from "@portal/components/primitives";
import { CheckboxOption } from "@portal/components/ui/CheckboxOption";
import { DataTable } from "@portal/components/ui/DataTable/DataTable";
import { SearchInput } from "@portal/components/ui/SearchInput";
import { type ReactNode, useState } from "react";
import { match, P } from "ts-pattern";
import { AdminTableLayout } from "../../layout/AdminTableLayout";
import { type DashboardScope, SCOPE_OPTIONS } from "../scope";
import { getActionItemTableColumns } from "./columns";
import { ActionItemWithNesting, useActionItems } from "./useActionItems";

type ActionItemViewProps = {
	topContent?: ReactNode;
	scope: DashboardScope;
	onScopeChange: (scope: DashboardScope) => void;
};

export const ActionItemView = ({ topContent, scope, onScopeChange }: ActionItemViewProps) => {
	const [showComplete, setShowComplete] = useState<boolean>(false);
	const [searchString, setSearchString] = useState<string>("");
	const { allActionItems, actionItemsIsLoading, addRecentlyCompleted, removeRecentlyCompleted } =
		useActionItems({
			showComplete,
			scope,
		});

	const actionItemTableColumns = getActionItemTableColumns({
		addRecentlyCompleted,
		removeRecentlyCompleted,
	});

	return (
		<AdminTableLayout
			topContent={topContent}
			title={
				<div className="w-full h-full">
					<div className="flex items-center justify-between h-full">
						<div className="flex items-center h-full space-x-[25px]">
							<div className="leading-[23px]">Action Items</div>
							<div className="flex items-center space-x-[12px]">
								<SearchInput
									className="w-[250px] bg-gray-50 border border-gray-200 focus-within:outline-gray-400 focus-within:shadow-none"
									inputClassName="placeholder:text-tertiary"
									placeholder="Search action items..."
									onChange={(value) => setSearchString(value)}
								/>
								<CheckboxOption
									checked={showComplete}
									onChange={() => setShowComplete(!showComplete)}
									label="Show completed"
									labelClassName="text-[14px] text-tertiary"
									condensed
								/>
							</div>
						</div>
						<SegmentedControls<DashboardScope>
							data-testid="dashboard-scope-toggle"
							value={scope}
							onChange={onScopeChange}
						>
							{SCOPE_OPTIONS.map((option) => (
								<SegmentedControls.Segment key={option.id} value={option.id}>
									{option.label}
								</SegmentedControls.Segment>
							))}
						</SegmentedControls>
					</div>
				</div>
			}
		>
			<div data-testid="admin-action-items" className="w-full h-full flex flex-col">
				{!actionItemsIsLoading && allActionItems.length === 0 ? (
					<div className="flex items-center justify-center text-tertiary italic h-64">
						No action items
					</div>
				) : (
					<DataTable
						name="action_items"
						columnDefs={actionItemTableColumns}
						getRowUrl={(row: ActionItemWithNesting) =>
							match(row)
								// We do not want to link to parent action items that have sub-items
								.with({ subItems: P.not(P.nullish) }, () => {
									return "";
								})
								.with({ relationType: "account" }, (actionItem) => {
									return `/accounts/${actionItem.relationId}?adminTool=actionItems&actionItemId=${actionItem.id}`;
								})
								.with({ relationType: "bond" }, (actionItem) => {
									return `/bonds/${actionItem.relationId}?adminTool=actionItems&actionItemId=${actionItem.id}`;
								})
								.with({ relationType: "quote" }, (actionItem) => {
									return `/requests/${actionItem.relationId}?adminTool=actionItems&actionItemId=${actionItem.id}`;
								})
								.exhaustive()
						}
						searchString={searchString}
						data={actionItemsIsLoading ? undefined : allActionItems}
						loadingMessage="Loading action items..."
					/>
				)}
			</div>
		</AdminTableLayout>
	);
};
